import { formatMoneyExact } from '@/lib/dateFormat';
import type { PurchaseEnvelope } from '@/types/oscar';

/**
 * A CEA envelope's approved / drawn / left, in words.
 *
 * <p>Anthony, 29/10/2026: a CEA cannot be raised on forecasts, so one CEA - an "envelope" - is raised
 * on a Capex forecast and each purchase is a cost line drawn from it. The IT Finance Approver picks
 * which; going over it WARNS and never blocks.</p>
 *
 * ⚠ ONE TEXT NODE, so it reads as one sentence to a screen reader and a test can find it whole.
 * ⚠ The figures are LIVE from the server, never kept: they move with every purchase.
 */
export function envelopeFigures(e: PurchaseEnvelope): string {
  const left = e.LeftAmount < 0
    ? `${formatMoneyExact(-e.LeftAmount)} over`
    : `${formatMoneyExact(e.LeftAmount)} left`;
  return `${formatMoneyExact(e.ApprovedAmount)} approved, ${formatMoneyExact(e.DrawnAmount)} drawn, ${left}`;
}

/**
 * The warning for spending {@code amount} on this envelope, or null when it fits.
 *
 * ⚠ A WARNING, NEVER A REFUSAL. This is the same sum the server does when the line is raised (it
 * says it back afterwards); showing it BEFORE the button is pressed is the point.
 */
export function envelopeWarning(e: PurchaseEnvelope, amount: number): string | null {
  const parts: string[] = [];

  if (!e.CeaApproved) {
    parts.push(`The CEA for this envelope is ${e.CeaStatus}, not Approved yet.`);
  }

  if (Number.isFinite(amount) && amount > 0) {
    const over = amount - Math.max(e.LeftAmount, 0);
    if (over > 0) {
      parts.push(
        e.LeftAmount <= 0
          ? `The envelope is already fully drawn, so this ${formatMoneyExact(amount)} is over it.`
          : `This is ${formatMoneyExact(over)} over what is left in the envelope (${formatMoneyExact(e.LeftAmount)}).`,
      );
      parts.push('It will be raised anyway - the CEA may need topping up.');
    }
  }

  return parts.length === 0 ? null : parts.join(' ');
}
